import React from 'react';
import { Platform, View, Text, StyleSheet } from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';
import { useTheme } from '../../../theme/useTheme';
import { AdminColors } from '../constants/adminColors';
import FontFamily from '../../../constants/fonts';

type BannerVariant = 'info' | 'warn';

const InfoIcon = ({ color }: { color: string }) => (
  <Svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <Circle cx="12" cy="12" r="10" />
    <Path d="M12 16v-4" />
    <Path d="M12 8h.01" />
  </Svg>
);

const WarnIcon = ({ color }: { color: string }) => (
  <Svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <Path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <Path d="M12 9v4" />
    <Path d="M12 17h.01" />
  </Svg>
);

interface AdminInfoBannerProps {
  message: string | React.ReactNode;
  variant?: BannerVariant;
  actions?: React.ReactNode;
}

const AdminInfoBanner: React.FC<AdminInfoBannerProps> = ({ message, variant = 'info', actions }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const containerStyle = variant === 'warn' ? styles.warn : styles.info;
  const textStyle = variant === 'warn' ? styles.warnText : styles.infoText;

  // Dark mode overrides
  const darkContainerStyle = isDark
    ? variant === 'warn'
      ? { backgroundColor: colors.statusBackground.warning, borderLeftColor: colors.status.warning }
      : { backgroundColor: colors.statusBackground.info, borderLeftColor: colors.brand.primary }
    : undefined;

  const iconColor = isDark
    ? variant === 'warn' ? colors.status.warning : colors.brand.primary
    : variant === 'warn' ? AdminColors.yellow200 : AdminColors.blue700;

  return (
    <View style={[styles.container, containerStyle, darkContainerStyle]}>
      {variant === 'info' ? <InfoIcon color={iconColor} /> : <WarnIcon color={iconColor} />}
      <Text style={[styles.message, isDark ? { color: variant === 'warn' ? colors.status.warning : colors.brand.primary } : textStyle]}>{message}</Text>
      {actions && <View style={styles.actions}>{actions}</View>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    marginBottom: 14,
    gap: 10,
    borderLeftWidth: 3,
  },
  info: {
    backgroundColor: AdminColors.blue75,
    borderLeftColor: AdminColors.blue700,
  },
  warn: {
    backgroundColor: AdminColors.yellow50,
    borderLeftColor: AdminColors.yellow200,
  },
  message: {
    flex: 1,
    fontSize: 13,
    fontWeight: '500',
    fontFamily: FontFamily.medium,
    lineHeight: 18,
  },
  infoText: {
    color: '#1E5A7A',
  },
  warnText: {
    color: AdminColors.textWarning,
  },
  actions: {
    flexDirection: 'row',
    gap: 6,
    flexShrink: 0,
  },
});

export default AdminInfoBanner;
